import "./CheckoutModal.css";

import Dialog from "@mui/material/Dialog";
import { QRCodeSVG } from "qrcode.react";
import React, { useEffect, useState } from "react";

import CustomerDetails, { displayDate } from "./CustomerDetails";
import PhoneInput from "./PhoneInput";

const numberFormat = new Intl.NumberFormat("mn-MN");
const messages = {
  pending: "Төлбөр хүлээгдэж байна. QPay ашиглан төлбөрөө төлнө үү.",
  paid: "Тасалбарын худалдан авалт амжилттай боллоо.",
  expired: "Төлбөрийн хугацаа дууссан байна. Шинэ төлбөр үүсгэнэ үү.",
  failed: "Төлбөр амжилтгүй боллоо. Дахин оролдоно уу.",
};

const CheckoutModal = ({
  open,
  onClose,
  selected,
  ticketCount,
  setTicketCount,
  phone,
  setPhone,
  customer,
  loadCustomer,
  customerLoading,
  sections,
  onCheckTickets,
  payment,
  busy,
  submitting,
  checking,
  error,
  buyTickets,
  checkPayment,
}) => {
  const [step, setStep] = useState(1);
  useEffect(() => {
    if (open) setStep(1);
  }, [open]);
  const validPhone = /^\d{8}$/.test(phone);
  const validCount =
    Number.isInteger(ticketCount) &&
    ticketCount >= 1 &&
    ticketCount <= selected?.ticket_balance;
  const links = Array.isArray(payment?.urls)
    ? payment.urls
    : Array.isArray(payment?.bankLinks)
    ? payment.bankLinks
    : [];
  const title = payment
    ? payment.status === "paid"
      ? "Худалдан авалт амжилттай"
      : "QPay"
    : step === 1
    ? "Утасны дугаар"
    : step === 2
    ? "Тасалбарын тоо"
    : "Баталгаажуулах";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby="megawin-checkout-title"
      maxWidth={false}
      PaperProps={{ className: "megawin-checkout" }}
    >
      <header className="megawin-checkout-header">
        <h2 id="megawin-checkout-title">{title}</h2>
        <div className="megawin-checkout-header-actions">
          {payment &&
            !["paid", "expired", "failed"].includes(payment.status) && (
              <button
                type="button"
                className="megawin-orange-button"
                disabled={busy}
                onClick={checkPayment}
              >
                {checking ? "Төлбөр шалгаж байна…" : "Төлбөр шалгах"}
              </button>
            )}
          <button
            type="button"
            aria-label="Хаах"
            disabled={busy}
            onClick={onClose}
            className="megawin-close"
          >
            ×
          </button>
        </div>
      </header>
      <div className="megawin-checkout-body">
        {!payment && step === 1 && (
          <>
            <PhoneInput id="checkout-phone" phone={phone} setPhone={setPhone} />
          </>
        )}

        {!payment && step === 2 && (
          <>
            <CustomerDetails
              customer={customer}
              sections={sections}
              busy={busy}
              onCheckTickets={onCheckTickets}
            />
            <label htmlFor="checkout-ticket-count">
              Худалдан авах тасалбарын тоо ширхэгийг оруулна уу.
            </label>
            <div className="megawin-quantity">
              <button
                type="button"
                aria-label="Тасалбар хасах"
                disabled={busy || ticketCount <= 1}
                onClick={() => setTicketCount(ticketCount - 1)}
              >
                −
              </button>
              <input
                id="checkout-ticket-count"
                type="number"
                min="1"
                max={selected?.ticket_balance}
                step="1"
                value={ticketCount}
                disabled={busy}
                onChange={(event) => setTicketCount(Number(event.target.value))}
              />
              <button
                type="button"
                aria-label="Тасалбар нэмэх"
                disabled={busy || ticketCount >= selected?.ticket_balance}
                onClick={() => setTicketCount(ticketCount + 1)}
              >
                +
              </button>
            </div>
            <p className="megawin-checkout-note">
              Үлдсэн: {numberFormat.format(selected?.ticket_balance || 0)}{" "}
              тасалбар
            </p>
            {!validCount && (
              <p role="alert" className="megawin-checkout-error">
                Тасалбарын тоог 1-ээс {selected?.ticket_balance} хүртэл бүхэл
                тоогоор оруулна уу.
              </p>
            )}
          </>
        )}
        {!payment && step === 3 && (
          <dl className="megawin-checkout-summary">
            <dt>Сугалааны нэр:</dt>
            <dd>{selected?.section_name}</dd>
            <dt>Тасалбарын тоо:</dt>
            <dd>{numberFormat.format(ticketCount)}</dd>
            <dt>Нийт төлөх дүн:</dt>
            <dd>
              {numberFormat.format((selected?.amount || 0) * ticketCount)}₮
            </dd>
            <dt>Утасны дугаар:</dt>
            <dd>{phone}</dd>
            <dt>Төлбөрийн нөхцөл:</dt>
            <dd>QPay</dd>
          </dl>
        )}
        {payment && (
          <>
            <button
              type="button"
              className="mw-back"
              disabled={busy}
              onClick={() => {
                setPhone("");
                setStep(1);
              }}
            >
              Утасны дугаар өөрчлөх
            </button>
            <p role="status">
              {messages[payment.status] || "Төлбөрийн төлөв шалгана уу."}
            </p>
            {payment.status === "pending" && (
              <>
                {payment.qrData && (
                  <div className="megawin-qr">
                    <QRCodeSVG
                      value={payment.qrData}
                      size={216}
                      marginSize={4}
                      style={{ width: "100%", height: "auto" }}
                      title="QPay төлбөрийн QR код"
                    />
                  </div>
                )}
                {payment.expires && (
                  <p className="megawin-checkout-note">
                    Дуусах хугацаа: {displayDate(payment.expires)}
                  </p>
                )}
                {Array.isArray(links) && links.length > 0 && (
                  <div className="megawin-bank-links">
                    {links
                      .filter(
                        (bank) =>
                          typeof bank?.link === "string" &&
                          /^[a-z][a-z0-9+.-]*:/i.test(bank.link) &&
                          !/^(javascript|data|vbscript):/i.test(bank.link)
                      )
                      .map((bank, index) => (
                        <a key={`${bank.link}-${index}`} href={bank.link}>
                          {bank.logo && /^https?:\/\//i.test(bank.logo) && (
                            <img src={bank.logo} alt="" />
                          )}
                          <span>
                            {bank.description || bank.name || "Банкны апп"}
                          </span>
                        </a>
                      ))}
                  </div>
                )}
              </>
            )}
            <p className="megawin-checkout-note">
              Захиалгын дугаар: {payment.checkoutId}
            </p>
            {payment.status === "paid" && (
              <CustomerDetails
                customer={customer}
                sections={sections}
                busy={busy}
                onCheckTickets={onCheckTickets}
              />
            )}
          </>
        )}
        {error && (
          <p role="alert" className="megawin-checkout-error">
            {error}
          </p>
        )}
      </div>
      <footer className="megawin-checkout-footer">
        {!payment ? (
          <>
            <button
              type="button"
              className="megawin-orange-button"
              disabled={busy}
              onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
            >
              Өмнөх
            </button>
            <span>Алхам {step} / 4</span>
            <button
              type="button"
              className="megawin-orange-button"
              disabled={busy || !validPhone || (step > 1 && !validCount)}
              onClick={async () => {
                if (step === 1) {
                  if (await loadCustomer()) setStep(2);
                } else if (step === 2) setStep(3);
                else buyTickets();
              }}
            >
              {customerLoading
                ? "Ачаалж байна…"
                : submitting
                ? "Төлбөр бэлдэж байна…"
                : "Дараах"}
            </button>
          </>
        ) : payment.status === "paid" ? (
          <>
            <button
              type="button"
              disabled={busy}
              className="megawin-orange-button"
              onClick={() => onCheckTickets(payment.checkoutId)}
            >
              Тасалбар шалгах
            </button>
            <button
              type="button"
              className="megawin-orange-button megawin-done"
              onClick={onClose}
            >
              Дуусгах
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="megawin-orange-button"
              disabled={busy}
              onClick={onClose}
            >
              Хаах
            </button>
            <span>Алхам 4 / 4</span>
            {["expired", "failed"].includes(payment.status) ? (
              <button
                type="button"
                className="megawin-orange-button"
                disabled={busy || !validPhone || !validCount}
                onClick={buyTickets}
              >
                Шинэ төлбөр үүсгэх
              </button>
            ) : null}
          </>
        )}
      </footer>
    </Dialog>
  );
};

export default CheckoutModal;
